import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { SimpleDialogProps } from './SimpleDialog.types';
import './SimpleDialog.scss';
import Header from './components/Header/Header';
import { StatLinkClassProp } from '@/utils/externalAnalyticsGM';

const SimpleDialog: React.FC<SimpleDialogProps> & {
  Header: React.FC<{ heading: string }>;
} = ({
  isOpen,
  onClose,
  children,
  width = 'auto',
  height = 'auto',
  maxWidth = '90vw',
  maxHeight = '90vh',
  className = '',
  backdropBlur = true,
  backdropDarkness = 'medium',
  showCloseButton = true,
  closeOnBackdropClick = true,
  closeOnEscape = true,
  title = '',
  dir,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      // Small delay to ensure the element is rendered before animating
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 10);
      return () => clearTimeout(timer);
    } else {
      setIsVisible(false);
      // Wait for animation to complete before unmounting
      const timer = setTimeout(() => {
        setShouldRender(false);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!closeOnEscape) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose, closeOnEscape]);

  useEffect(() => {
    // Prevent body scroll when dialog is open
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (closeOnBackdropClick && e.target === e.currentTarget) {
      onClose();
    }
  };

  const getBackdropClasses = () => {
    const baseClasses = 'simple-dialog-backdrop';
    const blurClass = backdropBlur ? 'simple-dialog-backdrop--blur' : '';
    const darknessClass = `simple-dialog-backdrop--${backdropDarkness}`;
    const visibleClass = isVisible ? 'simple-dialog-backdrop--visible' : '';

    return `${baseClasses} ${blurClass} ${darknessClass} ${visibleClass}`.trim();
  };

  const getDialogStyles = (): React.CSSProperties => {
    return {
      width: typeof width === 'number' ? `${width}px` : width,
      maxWidth: typeof maxWidth === 'number' ? `${maxWidth}px` : maxWidth,
    };
  };

  if (!shouldRender) return null;

  return createPortal(
    <div className={getBackdropClasses()} onClick={handleBackdropClick}>
      <div
        dir={dir}
        className={`simple-dialog ${
          isVisible ? 'simple-dialog--visible' : ''
        } ${className}`}
        style={getDialogStyles()}
        role="dialog"
        aria-modal="true"
      >
        {title && <h2 className="simple-dialog__title">{title}</h2>}
        {showCloseButton && (
          <button
            className={`simple-dialog__close ${StatLinkClassProp.statbuttonclose}`}
            onClick={onClose}
            aria-label="Close dialog"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        )}

        <div className="simple-dialog__content">{children}</div>
      </div>
    </div>,
    document.body
  );
};

SimpleDialog.Header = Header;

export default SimpleDialog;
